/**
 * Serviço para gerenciar meta tags dinamicamente em toda a aplicação
 * Isso permite que thumbnails funcionem em qualquer URL da aplicação
 */

// URLs das imagens para thumbnails
const S3_IMAGE =
  "https://exclusivelounge-production.s3.amazonaws.com/media/Feb-2024/8c09087c-c397-4a09-9c07-0f6faa19f6ea.png";
const LOCAL_IMAGE =
  window.location.origin + "/8c09087c-c397-4a09-9c07-0f6faa19f6ea.png";

// Por padrão, tentamos primeiro a imagem do S3
const DEFAULT_IMAGE = S3_IMAGE;

// ID do aplicativo Facebook (substitua pelo ID real do seu aplicativo)
const FB_APP_ID = "SEU_FACEBOOK_APP_ID"; // Substitua pelo ID real do seu aplicativo Facebook

// Interface para as opções de meta tags
interface MetaTagOptions {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
}

/**
 * Atualiza as meta tags para compartilhamento em redes sociais
 * @param options - Opções para as meta tags
 */
export function updateMetaTags(options: MetaTagOptions = {}) {
  const title = "Exclusive Lounge";
  const description = "Social Media for CIO's";
  const image = options.image || DEFAULT_IMAGE;
  const url = options.url || window.location.href;

  // Atualiza o título da página
  document.title = title;

  // Função para atualizar ou criar uma meta tag
  const updateMetaTag = (selector: string, attr: string, content: string) => {
    let element = document.querySelector(selector);
    if (!element) {
      const tag = selector.split("[")[0];
      element = document.createElement(tag);

      if (selector.includes("[")) {
        const attrMatch = selector.match(/\[(.*?)=/);
        const valueMatch = selector.match(/=(.*?)\]/);

        if (attrMatch && valueMatch) {
          const attrName = attrMatch[1].replace(/"/g, "");
          const attrValue = valueMatch[1].replace(/"/g, "");
          element.setAttribute(attrName, attrValue);
        }
      }

      document.head.appendChild(element);
    }

    element.setAttribute(attr, content);
  };

  // Meta tags básicas
  updateMetaTag('meta[name="title"]', "content", title);
  updateMetaTag('meta[name="description"]', "content", description);

  // Meta tags para Schema.org (Google)
  updateMetaTag('meta[itemprop="name"]', "content", title);
  updateMetaTag('meta[itemprop="description"]', "content", description);
  updateMetaTag('meta[itemprop="image"]', "content", image);

  // Meta tags para Open Graph (Facebook, Instagram, Discord, LinkedIn)
  updateMetaTag('meta[property="og:type"]', "content", "website");
  updateMetaTag('meta[property="og:url"]', "content", url);
  updateMetaTag('meta[property="og:title"]', "content", title);
  updateMetaTag('meta[property="og:description"]', "content", description);
  updateMetaTag('meta[property="og:image"]', "content", image);
  updateMetaTag('meta[property="og:image:secure_url"]', "content", image);
  updateMetaTag('meta[property="og:image:type"]', "content", "image/png");
  updateMetaTag('meta[property="og:image:width"]', "content", "1200");
  updateMetaTag('meta[property="og:image:height"]', "content", "630");
  updateMetaTag('meta[property="og:image:width:alt"]', "content", "1080");
  updateMetaTag('meta[property="og:image:height:alt"]', "content", "1080");
  updateMetaTag('meta[property="og:site_name"]', "content", "Exclusive Lounge");
  updateMetaTag('meta[property="og:locale"]', "content", "pt_BR");
  updateMetaTag(
    'meta[property="og:image:alt"]',
    "content",
    "Exclusive Lounge Logo"
  );
  updateMetaTag(
    'meta[property="og:updated_time"]',
    "content",
    new Date().toISOString()
  );
  // Adiciona a tag fb:app_id obrigatória para o Facebook
  updateMetaTag('meta[property="fb:app_id"]', "content", FB_APP_ID);

  // Meta tags para Twitter
  updateMetaTag(
    'meta[property="twitter:card"]',
    "content",
    "summary_large_image"
  );
  updateMetaTag('meta[property="twitter:url"]', "content", url);
  updateMetaTag('meta[property="twitter:title"]', "content", title);
  updateMetaTag('meta[property="twitter:description"]', "content", description);
  updateMetaTag('meta[property="twitter:image"]', "content", image);
  updateMetaTag(
    'meta[name="twitter:image:alt"]',
    "content",
    "Exclusive Lounge Logo"
  );

  // Meta tags para WhatsApp
  updateMetaTag('link[rel="image_src"]', "href", image);
  updateMetaTag('link[rel="canonical"]', "href", url);

  // Meta tags para aplicativos de mensagens
  updateMetaTag('meta[property="al:ios:url"]', "content", url);
  updateMetaTag('meta[property="al:android:url"]', "content", url);

  // Verifica se a imagem está acessível
  checkImageAccessibility(image);
}

/**
 * Verifica se a imagem está acessível e tenta usar uma imagem de fallback se necessário
 * @param imageUrl - URL da imagem para verificar
 */
function checkImageAccessibility(imageUrl: string): void {
  // Se a imagem já é a local, não precisamos verificar
  if (imageUrl === LOCAL_IMAGE) {
    return;
  }

  const img = new Image();
  img.onerror = () => {
    // Se a imagem falhou e é a do S3, tenta a local
    if (imageUrl === S3_IMAGE) {
      updateImageFallback(LOCAL_IMAGE);
    }
  };
  img.onload = () => {
    // Mesmo que a imagem carregue, vamos garantir que ela esteja disponível para compartilhamento
    // criando uma cópia local para cache
    if (imageUrl === S3_IMAGE) {
      // Cria uma imagem no DOM para garantir que esteja em cache
      const cachedImg = document.createElement("img");
      cachedImg.src = imageUrl;
      cachedImg.style.position = "absolute";
      cachedImg.style.top = "-9999px";
      cachedImg.style.left = "-9999px";
      cachedImg.style.width = "1px";
      cachedImg.style.height = "1px";
      document.body.appendChild(cachedImg);
    }
  };
  img.src = imageUrl;
}

/**
 * Atualiza para uma imagem de fallback se a imagem principal não estiver disponível
 * @param originalUrl - URL original da imagem
 */
function updateImageFallback(fallbackUrl: string): void {
  // Cria uma imagem de fallback no DOM para garantir que exista
  const fallbackImg = document.createElement("img");
  fallbackImg.src = fallbackUrl;
  fallbackImg.style.position = "absolute";
  fallbackImg.style.top = "-9999px";
  fallbackImg.style.left = "-9999px";
  document.body.appendChild(fallbackImg);

  // Atualiza todas as meta tags de imagem
  const imageMetaTags = [
    { selector: 'meta[property="og:image"]', attr: "content" },
    { selector: 'meta[property="og:image:secure_url"]', attr: "content" },
    { selector: 'meta[itemprop="image"]', attr: "content" },
    { selector: 'meta[property="twitter:image"]', attr: "content" },
    { selector: 'link[rel="image_src"]', attr: "href" },
  ];

  imageMetaTags.forEach((tag) => {
    const element = document.querySelector(tag.selector);
    if (element) {
      element.setAttribute(tag.attr, fallbackUrl);
    }
  });

  // Verifica se a imagem de fallback está acessível
  const testImg = new Image();
  testImg.onerror = () => {
    // Se mesmo a imagem local falhar, tentamos usar um data URI básico
    const dataUri =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P+/HgAFeAJ5jUTrAwAAAABJRU5ErkJggg==";

    imageMetaTags.forEach((tag) => {
      const element = document.querySelector(tag.selector);
      if (element) {
        element.setAttribute(tag.attr, dataUri);
      }
    });
  };
  testImg.src = fallbackUrl;
}

export default {
  updateMetaTags,
};
